// src/utils/slotLockAdminUtil.js
//
// Read and clear slotLocks from the admin side (Error Logs -> Slot locks).
//
// Separate from bookingAvailabilityUtil.js on purpose: that file owns the
// BOOKING flow's locks — claim, release, promote, always scoped to one
// booking id. This one is the operator's view: every lock in the database,
// whatever booking it belongs to, and the ability to delete locks that no
// longer have a live booking behind them.

import { ref, get } from 'firebase/database';
import { removeSlotLocksWhere } from './bookingAvailabilityUtil';

/** A lock whose expiry has passed is claimable; one with no expiry never is. */
export const LOCK_STATES = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
  PERMANENT: 'permanent',
};

/**
 * Reads a lock's `expiresAt` as epoch milliseconds.
 *
 * Tolerates the ISO strings written before the expiry fix (see toExpiryMs in
 * bookingAvailabilityUtil.js) so this screen can still show — and clear —
 * those legacy locks, which are exactly the ones an operator needs to find.
 *
 * @param {*} value
 * @returns {number|null} epoch ms, or null when absent/unparseable.
 */
export const readExpiryMs = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const ms = typeof value === 'number' ? value : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
};

/**
 * Classifies one lock for display.
 *
 * @param {{bookingId: string, expiresAt?: string|number}} lock
 * @param {number} [nowMs=Date.now()]
 * @returns {string} a LOCK_STATES value.
 */
export const classifyLock = (lock, nowMs = Date.now()) => {
  const expiry = readExpiryMs(lock?.expiresAt);
  // No expiry means the booking was promoted past the payment window — it is
  // a real reservation and only comes off when the booking is cancelled.
  if (expiry === null) return LOCK_STATES.PERMANENT;
  return expiry < nowMs ? LOCK_STATES.EXPIRED : LOCK_STATES.ACTIVE;
};

/**
 * Every slot lock in the database, flattened and grouped by date.
 *
 * One `get` on the whole `slotLocks` node rather than a query per date:
 * the node holds one entry per held court-hour and is pruned as bookings
 * complete, so it is small — and an operator looking for a stuck lock does
 * not know which date to ask for, which is the whole reason for this screen.
 *
 * @param {object} db - Firebase Realtime Database instance.
 * @param {number} [nowMs=Date.now()] - evaluation instant, injectable for tests.
 * @returns {Promise<Array<{date: string, locks: Array<object>, counts: object}>>}
 *   newest date first; each lock is
 *   { date, timeSlot, court, bookingId, expiresAt, expiryMs, state }.
 */
export const fetchSlotLockGroups = async (db, nowMs = Date.now()) => {
  if (!db) return [];
  const snapshot = await get(ref(db, 'slotLocks'));
  const all = snapshot.val() || {};

  return Object.entries(all)
    .map(([date, slots]) => {
      const locks = [];
      Object.entries(slots || {}).forEach(([timeSlot, courts]) => {
        Object.entries(courts || {}).forEach(([court, lock]) => {
          if (!lock) return;
          locks.push({
            date,
            timeSlot,
            court,
            bookingId: lock.bookingId || '',
            expiresAt: lock.expiresAt ?? null,
            expiryMs: readExpiryMs(lock.expiresAt),
            state: classifyLock(lock, nowMs),
          });
        });
      });

      const counts = locks.reduce((acc, lock) => {
        acc[lock.state] = (acc[lock.state] || 0) + 1;
        return acc;
      }, {});

      return { date, locks, counts };
    })
    .filter(group => group.locks.length > 0)
    .sort((a, b) => b.date.localeCompare(a.date));
};

/**
 * Removes locks from one date.
 *
 * Delegates to removeSlotLocksWhere, which deletes single-lock paths so the
 * per-lock database rules apply (an admin login may clear any lock).
 *
 * @param {object} db
 * @param {string} date - "yyyy-MM-dd".
 * @param {(lock: {timeSlot: string, court: string, bookingId: string, expiresAt: *}) => boolean} shouldDrop
 *   returns true for each lock to delete.
 * @returns {Promise<number>} how many locks were removed.
 */
export const deleteSlotLocksWhere = (db, date, shouldDrop) => removeSlotLocksWhere(db, date, shouldDrop);

/**
 * Deletes one specific lock.
 *
 * @param {object} db
 * @param {{date: string, timeSlot: string, court: string}} lock
 * @returns {Promise<number>} 1 when it was there, 0 when already gone.
 */
export const deleteSlotLock = (db, { date, timeSlot, court }) => (
  deleteSlotLocksWhere(db, date, (candidate) => (
    candidate.timeSlot === timeSlot && String(candidate.court) === String(court)
  ))
);

/**
 * Deletes every lock on one date.
 *
 * @param {object} db
 * @param {string} date
 * @returns {Promise<number>} how many were removed.
 */
export const deleteSlotLocksForDate = (db, date) => deleteSlotLocksWhere(db, date, () => true);

/**
 * Deletes every lock on one date whose expiry has already passed, leaving
 * live claims and promoted (no-expiry) reservations untouched.
 *
 * This is the safe bulk action: an expired lock is one the booking flow was
 * already entitled to take over, so removing it changes nothing a guest
 * could still be relying on.
 *
 * @param {object} db
 * @param {string} date
 * @param {number} [nowMs=Date.now()]
 * @returns {Promise<number>} how many were removed.
 */
export const deleteExpiredSlotLocksForDate = (db, date, nowMs = Date.now()) => (
  deleteSlotLocksWhere(db, date, (candidate) => classifyLock(candidate, nowMs) === LOCK_STATES.EXPIRED)
);
